<template>
  <div class="snapshot-history">
    <div class="snapshot-history__heading">
      <div>
        <h2 class="mb-25">Histórico de alterações do snapshot</h2>
        <p class="text-muted mb-0">
          Inclusões, alterações e exclusões registradas na divisão de comissão
          deste contrato, da mais recente para a mais antiga.
        </p>
      </div>
      <span
        v-if="totalItems"
        class="snapshot-history__count"
      >
        <strong>{{ totalItems }}</strong>
        {{ totalItems === 1 ? "alteração" : "alterações" }}
      </span>
    </div>

    <div
      v-if="loading"
      class="d-flex flex-column align-items-center justify-content-center p-4"
    >
      <b-spinner variant="primary" />
      <span class="text-muted mt-1">Carregando alterações…</span>
    </div>

    <b-card
      v-else-if="items.length"
      no-body
      class="snapshot-history__card"
    >
      <b-table
        :items="items"
        :fields="tableColumns"
        striped
        borderless
        responsive
        class="mb-0"
      >
        <template #cell(created_at)="data">
          <span class="text-nowrap">{{ data.item.created_at | dateTime }}</span>
        </template>

        <template #cell(performed_by)="data">
          <div class="d-flex flex-column">
            <span>{{ performerName(data.item) }}</span>
            <b-badge
              v-if="data.item.performed_by.is_system"
              variant="light-secondary"
              class="snapshot-history__origin mt-25"
            >
              Rotina automática
            </b-badge>
          </div>
        </template>

        <template #cell(event)="data">
          <b-badge
            :variant="eventVariant(data.item)"
            class="snapshot-history__event"
          >
            {{ eventLabel(data.item) }}
          </b-badge>
        </template>

        <template #cell(changes)="data">
          <span v-if="!data.item.changes.length" class="text-muted">
            Sem detalhe disponível na origem
          </span>

          <div v-else-if="data.item.event === 'updated'" class="snapshot-history__changes">
            <template v-for="change in data.item.changes">
              <span
                :key="`${data.item.id}-${change.field}-label`"
                class="snapshot-history__field"
              >
                {{ change.label }}
              </span>
              <span
                :key="`${data.item.id}-${change.field}-value`"
                class="text-primary"
              >
                {{ formatValue(change, "previous") }}
                <feather-icon icon="ArrowRightIcon" size="12" class="mx-25" />
                {{ formatValue(change, "current") }}
              </span>
            </template>
          </div>

          <span v-else class="text-primary">{{ lineSummary(data.item) }}</span>
        </template>

        <template #cell(id_snapshot)="data">
          <span class="text-muted">{{ data.item.id_snapshot || "—" }}</span>
        </template>
      </b-table>

      <div class="mx-2 my-1">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de
              {{ totalItems }} itens
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="get"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <div v-else class="snapshot-history__empty">
      <span class="snapshot-history__empty-icon">
        <feather-icon icon="ClockIcon" size="24" />
      </span>
      <h3>Nenhuma alteração de snapshot</h3>
      <p>
        Nenhuma alteração de snapshot foi localizada para este contrato.
      </p>
    </div>
  </div>
</template>

<script>
import {
  BBadge, BCard, BCol, BPagination, BRow, BSpinner, BTable,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as sharedTypes from '@/modules/shared/store/types'
import * as types from '../store/types'

export default {
  components: {
    BBadge,
    BCard,
    BCol,
    BPagination,
    BRow,
    BSpinner,
    BTable,
  },
  setup() {
    return { toast: useToast() }
  },
  props: {
    contractId: {
      required: true,
      type: [Number, String],
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      totalItems: 0,
      pageItemsFrom: 0,
      pageItemsTo: 0,
      currentPage: 1,
      itemsPerPage: 10,
      tableColumns: [
        { key: 'created_at', label: 'Data' },
        { key: 'performed_by', label: 'Responsável' },
        { key: 'event', label: 'Tipo' },
        { key: 'changes', label: 'Detalhe' },
        { key: 'id_snapshot', label: 'Linha do snapshot' },
      ],
    }
  },
  computed: {
    ...mapGetters({ initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE }),
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage || this.itemsPerPage
    this.get()
  },
  methods: {
    ...mapActions({ getSnapshotHistory: types.GET_SNAPSHOT_HISTORY }),
    performerName(item) {
      const { name, id } = item.performed_by
      if (name) return name
      return id ? `Usuário ${id}` : 'Não identificado'
    },
    eventLabel(item) {
      if (item.event === 'created') return 'Linha incluída'
      if (item.event === 'removed') return 'Linha removida'
      return 'Alteração'
    },
    eventVariant(item) {
      if (item.event === 'created') return 'light-success'
      if (item.event === 'removed') return 'light-danger'
      return 'light-info'
    },
    lineSummary(item) {
      return item.changes
        .map(change => this.formatValue(change, change.current === null ? 'previous' : 'current'))
        .join(' · ')
    },
    formatValue(change, key) {
      const value = change[key]
      if (value === null || value === undefined || value === '') return '—'
      if (change.field === 'percentage_value') {
        return `${this.$options.filters.toDecimal(Number(value))}%`
      }
      return value
    },
    get(page) {
      this.loading = true
      this.getSnapshotHistory({
        id: this.contractId,
        limit: this.itemsPerPage,
        page: Number.isInteger(page) ? page : this.currentPage,
      })
        .then(response => {
          const { data } = response
          this.items = data.data || []
          this.totalItems = data.total || 0
          this.pageItemsFrom = data.from || 0
          this.pageItemsTo = data.to || 0
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao buscar o histórico de alterações do snapshot. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => { this.loading = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.snapshot-history { padding-bottom: 1rem; }

.snapshot-history__heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin: 0 0.5rem 1rem;
}

.snapshot-history__heading h2 { font-size: 1.35rem; }

.snapshot-history__count {
  padding: 0.55rem 0.85rem;
  border-radius: 99px;
  color: #6e6b7b;
  background: rgba(115, 103, 240, 0.08);
  white-space: nowrap;
}

.snapshot-history__count strong { color: #7367f0; }

.snapshot-history__card {
  overflow: hidden;
  margin: 0 0.5rem;
  border: 1px solid rgba(110, 107, 123, 0.12);
  border-radius: 0.85rem;
  box-shadow: 0 10px 32px rgba(34, 41, 47, 0.07);
}

.snapshot-history__changes {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 1rem;
  row-gap: 0.35rem;
  align-items: baseline;
}

.snapshot-history__field { font-weight: 500; }
.snapshot-history__origin { font-size: 0.68rem; align-self: flex-start; }
.snapshot-history__event { font-size: 0.72rem; white-space: nowrap; }

.snapshot-history__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 480px;
  margin: 2.5rem auto;
  padding: 1.5rem;
  color: #6e6b7b;
  text-align: center;
}

.snapshot-history__empty-icon {
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  margin-bottom: 1rem;
  border-radius: 18px;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.12);
}

.snapshot-history__empty h3 { color: #2f2b3d; font-size: 1.15rem; }

@media (max-width: 767.98px) {
  .snapshot-history__heading { align-items: stretch; flex-direction: column; }
  .snapshot-history__count { align-self: flex-start; }
  .snapshot-history__changes { grid-template-columns: 1fr; row-gap: 0; }
}
</style>

<style lang="scss">
.dark-layout {
  .snapshot-history {
    .snapshot-history__heading p,
    .snapshot-history__empty { color: #b4b7bd !important; }

    .snapshot-history__count {
      color: #c7c9d1;
      background: rgba(115, 103, 240, 0.16);
    }

    .snapshot-history__card {
      border-color: rgba(207, 208, 226, 0.1);
      background: #283046;
      box-shadow: 0 12px 34px rgba(10, 13, 22, 0.28);
    }

    .snapshot-history__empty h3 { color: #f1f1f4; }
  }
}
</style>
